// Vehicle plate → kendaraan + penduduk join with simple in-memory cache.
import { supabase } from './supabase';

const POS_TTL = 5 * 60 * 1000;  // positive cache 5 min
const NEG_TTL = 60 * 1000;      // negative cache 1 min (allow re-check)
const cache = new Map();         // plate(uppercased) → { v: result|null, t: ms }

// "Pemilik: AHMAD DAHLAN — Kendaraan Listrik (EV)" → "AHMAD DAHLAN"
export function parsePemilikFromCatatan(catatan) {
  if (typeof catatan !== 'string') return null;
  const m = catatan.match(/^Pemilik:\s*([^—]+?)(?:\s*—\s*|$)/);
  return m ? m[1].trim() : null;
}

function ttlFor(value) { return value ? POS_TTL : NEG_TTL; }

// Levenshtein distance (small strings, OK O(n*m))
function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 99;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i-1] === b[j-1]
        ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j-1], dp[i-1][j], dp[i][j-1]);
    }
  }
  return dp[m][n];
}

// Find plate in DB list with edit distance ≤2 (handles common OCR confusion).
// Strip spaces sebelum compare — format spasi sering OCR mistake.
// Threshold ≤1 (previously ≤2) untuk reduce false positives — distance 2
// menyebabkan 2 plat berbeda dapat dianggap match (false positive lihat
// user feedback Sprint 004: B 1522 QGK / B 1522 PXZ both matched ke same).
// Min length 5 untuk reject parsial OCR (e.g. "AAA" tidak boleh match plat penuh).
function findCloseMatch(query, list) {
  const qNorm = query.replace(/\s+/g, '');
  if (qNorm.length < 5) return null; // OCR baca terlalu sedikit char → reject
  let best = null, bestDist = 2; // accept ≤1 only (loop init=2, break if d<bestDist)
  for (const p of list) {
    const pNorm = (p || '').replace(/\s+/g, '');
    const d = editDistance(qNorm, pNorm);
    if (d < bestDist) {
      bestDist = d;
      best = p;
      if (d === 0) break;
    }
  }
  return bestDist <= 1 ? best : null;
}

export function clearVehicleCache() { cache.clear(); }

export async function lookupVehicle(plate) {
  if (!plate || typeof plate !== 'string') return null;
  const key = plate.trim().toUpperCase();
  if (!key) return null;

  const hit = cache.get(key);
  if (hit && Date.now() - hit.t < ttlFor(hit.v)) return hit.v;

  // 1. Find kendaraan FULL turunan — pakai `*` supaya semua kolom (incl. yang
  // ditambahkan migration baru no_registrasi/status_stnk/tnkb_url/bpkb_url/foto_url)
  // otomatis ikut, tanpa breakage kalau migration belum applied.
  let { data: k, error: kErr } = await supabase.from('kendaraan')
    .select('*')
    .ilike('no_plat', key)
    .maybeSingle();
  if (kErr) console.warn('[lookupVehicle] kendaraan:', kErr.message);

  // 1b. CLOSE-MATCH boost (F0-A7) — kalau exact match gagal, coba edit-distance ≤2
  // Common OCR error: 8↔B, 0↔O, 5↔S, 1↔I. Cari plat yang mirip di DB.
  if (!k) {
    const { data: allPlats } = await supabase.from('kendaraan').select('no_plat');
    if (Array.isArray(allPlats)) {
      const candidate = findCloseMatch(key, allPlats.map(r => r.no_plat));
      if (candidate) {
        const { data: k2 } = await supabase.from('kendaraan')
          .select('*')
          .eq('no_plat', candidate)
          .maybeSingle();
        if (k2) {
          k = k2;
          console.log(`[lookupVehicle] close-match: '${plate}' → '${candidate}' (DB)`);
        }
      }
    }
  }

  if (!k) {
    cache.set(key, { v: null, t: Date.now() });
    return null;
  }

  let pemilik_data = null; // FULL KTP row dari penduduk
  let nama_pemilik = null;

  // 2. If FK NIK exists, fetch FULL penduduk KTP row
  if (k.nik_pemilik) {
    const { data: p, error: pErr } = await supabase.from('penduduk')
      .select('*')
      .eq('nik', k.nik_pemilik)
      .maybeSingle();
    if (pErr) console.warn('[lookupVehicle] penduduk:', pErr.message);
    if (p) {
      pemilik_data = p;
      nama_pemilik = p.nama_lengkap || null;
    }
  }

  // 3. Fallback nama_pemilik dari catatan_visual
  if (!nama_pemilik) {
    nama_pemilik = parsePemilikFromCatatan(k.catatan_visual);
  }

  const result = {
    no_plat:           k.no_plat,
    merk_model:        k.merk_model,
    warna:             k.warna,
    tipe_kendaraan:    k.tipe_kendaraan,
    tahun:             k.tahun,
    no_rangka:         k.no_rangka,
    no_mesin:          k.no_mesin,
    nik_pemilik:       k.nik_pemilik,
    masa_berlaku_stnk: k.masa_berlaku_stnk,
    catatan_visual:    k.catatan_visual,
    // STNK + dokumen tambahan (migration 20260503) — null kalau belum applied.
    no_registrasi:     k.no_registrasi  ?? null,
    tgl_registrasi:    k.tgl_registrasi ?? null,
    status_stnk:       k.status_stnk    ?? null,
    tnkb_url:          k.tnkb_url       ?? null,
    bpkb_url:          k.bpkb_url       ?? null,
    foto_url:          k.foto_url       ?? null,  // foto kendaraan (BUKAN pemilik)
    nama_pemilik:      nama_pemilik || null,
    pemilik:           pemilik_data,        // FULL KTP row pemilik
    foto_pemilik:      pemilik_data?.foto_url || null,
    intel:             pemilik_data?.status_intelijen || null,
  };

  cache.set(key, { v: result, t: Date.now() });
  return result;
}
